import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogContent, DialogFooter, DialogTrigger, Flex, Heading, LoadingOrbit, Text, TextField, PasswordField, useToast } from '@chatsdet/ui';
import { ShieldCheck, Plus, Trash, Globe, CheckCircle, Warning, ArrowsClockwise, X } from '@phosphor-icons/react';
import { API_URL } from '../../../config.js';

type CloudflareAccessIntegration = {
  id: string;
  name: string;
  appDomain: string;
  lastValidatedAt: string | null;
  createdAt: string;
};

type CloudflareAccessSectionProps = {
  isAdmin: boolean;
};

export function CloudflareAccessSection({ isAdmin }: CloudflareAccessSectionProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingIntegration, setEditingIntegration] = React.useState<CloudflareAccessIntegration | null>(null);
  const [validatingId, setValidatingId] = React.useState<string | null>(null);
  const [validationError, setValidationError] = React.useState<{ integrationId: string; message: string } | null>(null);

  // Form state
  const [formData, setFormData] = React.useState({
    name: '',
    appDomain: '',
    clientId: '',
    clientSecret: '',
  });

  // Fetch CF Access integrations
  const integrationsQuery = useQuery({
    queryKey: ['cloudflare-access-integrations'],
    queryFn: async (): Promise<{ ok: boolean; data: CloudflareAccessIntegration[] }> => {
      const res = await fetch(`${API_URL}/integrations/cloudflare-access`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to fetch integrations');
      }
      return res.json();
    },
    enabled: isAdmin,
  });

  // Create integration
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch(`${API_URL}/integrations/cloudflare-access`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        throw new Error(result.error || 'Failed to create integration');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('Cloudflare Access integration created');
      setIsCreateOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['cloudflare-access-integrations'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update integration
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const res = await fetch(`${API_URL}/integrations/cloudflare-access/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        throw new Error(result.error || 'Failed to update integration');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('Integration updated');
      setEditingIntegration(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['cloudflare-access-integrations'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Delete integration
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/integrations/cloudflare-access/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        throw new Error(result.error || 'Failed to delete integration');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('Integration deleted');
      queryClient.invalidateQueries({ queryKey: ['cloudflare-access-integrations'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Validate integration
  const validateMutation = useMutation({
    mutationFn: async (id: string) => {
      setValidatingId(id);
      setValidationError(null);
      const res = await fetch(`${API_URL}/integrations/cloudflare-access/${id}/validate`, {
        method: 'POST',
        credentials: 'include',
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        return { ok: false, error: result.error || 'Validation failed', integrationId: id };
      }
      return { ...result, integrationId: id };
    },
    onSuccess: (result) => {
      setValidatingId(null);
      if (result.ok) {
        setValidationError(null);
        toast.success('Authentication successful');
      } else {
        setValidationError({ integrationId: result.integrationId, message: result.error });
      }
      queryClient.invalidateQueries({ queryKey: ['cloudflare-access-integrations'] });
    },
    onError: (error: Error) => {
      setValidatingId(null);
      setValidationError({ integrationId: validatingId || '', message: error.message });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      appDomain: '',
      clientId: '',
      clientSecret: '',
    });
  };

  const handleOpenCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const handleOpenEdit = (integration: CloudflareAccessIntegration) => {
    setFormData({
      name: integration.name,
      appDomain: integration.appDomain,
      clientId: '', // Never pre-fill secrets
      clientSecret: '',
    });
    setEditingIntegration(integration);
  };

  const handleSubmit = () => {
    if (editingIntegration) {
      // For update, only include non-empty credentials
      const updateData: Partial<typeof formData> = {
        name: formData.name,
        appDomain: formData.appDomain,
      };
      if (formData.clientId.trim()) {
        updateData.clientId = formData.clientId;
      }
      if (formData.clientSecret.trim()) {
        updateData.clientSecret = formData.clientSecret;
      }
      updateMutation.mutate({ id: editingIntegration.id, data: updateData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (integration: CloudflareAccessIntegration) => {
    if (window.confirm(`Are you sure you want to delete "${integration.name}"? Credentials using this integration will fall back to AI-driven login.`)) {
      deleteMutation.mutate(integration.id);
    }
  };

  const integrations = integrationsQuery.data?.data ?? [];
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isDialogOpen = isCreateOpen || !!editingIntegration;

  // Check if form is valid for submission
  const isFormValid = editingIntegration
    ? formData.name.trim() && formData.appDomain.trim() // Edit: credentials optional
    : formData.name.trim() && formData.appDomain.trim() && formData.clientId.trim() && formData.clientSecret.trim(); // Create: all required

  if (!isAdmin) {
    return (
      <Card padding="lg">
        <CardContent>
          <Text className="text-muted-foreground">
            Only organization admins can manage Cloudflare Access integrations.
          </Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Box className="space-y-6">
        <Card padding="lg">
          <CardHeader>
            <Flex align="start" gap="md">
              <div className="p-2 bg-primary/10 rounded-lg">
                <ShieldCheck size={24} className="text-primary" weight="bold" />
              </div>
              <div className="flex-1">
                <Heading level={3}>Cloudflare Access Integrations</Heading>
                <Text size="sm" className="text-muted-foreground mt-1">
                  Configure service tokens to authenticate with Cloudflare Access protected applications
                </Text>
              </div>
              <Button variant="primary" onPress={handleOpenCreate}>
                <Plus size={16} className="mr-2" />
                Add Integration
              </Button>
            </Flex>
          </CardHeader>
          <CardContent>
            {integrationsQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-8">
                <LoadingOrbit size="md" />
              </Flex>
            ) : integrations.length > 0 ? (
              <div className="space-y-3">
                {integrations.map((integration) => (
                  <div key={integration.id} className="space-y-2">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Text weight="medium">{integration.name}</Text>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <Flex align="center" gap="xs">
                            <Globe size={14} className="text-muted-foreground" />
                            <Text size="sm" className="text-muted-foreground font-mono">
                              {integration.appDomain}
                            </Text>
                          </Flex>
                          {integration.lastValidatedAt && (
                            <Flex align="center" gap="xs">
                              <CheckCircle size={14} className="text-success" />
                              <Text size="xs" className="text-muted-foreground">
                                Validated {new Date(integration.lastValidatedAt).toLocaleDateString()}
                              </Text>
                            </Flex>
                          )}
                        </div>
                      </div>
                      <Flex gap="sm">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Validate"
                          onPress={() => validateMutation.mutate(integration.id)}
                          isDisabled={validatingId === integration.id}
                        >
                          <ArrowsClockwise
                            size={16}
                            className={validatingId === integration.id ? 'animate-spin' : ''}
                          />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onPress={() => handleOpenEdit(integration)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Delete"
                          onPress={() => handleDelete(integration)}
                          isDisabled={deleteMutation.isPending}
                        >
                          <Trash size={16} className="text-destructive" />
                        </Button>
                      </Flex>
                    </div>
                    {validationError?.integrationId === integration.id && (
                      <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                        <Warning size={18} className="text-destructive shrink-0 mt-0.5" weight="fill" />
                        <div className="flex-1 min-w-0">
                          <Text size="sm" className="text-destructive">
                            {validationError.message}
                          </Text>
                        </div>
                        <Button
                          variant="icon"
                          size="sm"
                          iconOnly
                          tooltip="Dismiss"
                          onPress={() => setValidationError(null)}
                          className="shrink-0"
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ShieldCheck size={48} className="mx-auto text-muted-foreground mb-4" weight="light" />
                <Text className="text-muted-foreground">No Cloudflare Access integrations</Text>
                <Text size="sm" className="text-muted-foreground mt-2 max-w-md mx-auto">
                  Add a service token to authenticate with Cloudflare Access protected applications without AI-driven login.
                </Text>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card padding="md">
          <CardContent>
            <Flex gap="md" align="start">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg shrink-0">
                <Warning size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div className="space-y-3">
                <Text size="sm" weight="medium">How it works</Text>
                <Text size="sm" className="text-muted-foreground">
                  Cloudflare Access service tokens allow automated authentication without browser interaction.
                  When a credential is linked to an integration, the system will use the service token to
                  obtain a <code className="bg-muted px-1 rounded text-xs">CF_Authorization</code> cookie
                  instead of performing AI-driven login.
                </Text>

                <Text size="sm" weight="medium" className="pt-2">Cloudflare Setup</Text>
                <div className="text-sm text-muted-foreground space-y-2">
                  <div>
                    <strong>1. Create a Service Token:</strong> In Zero Trust {'>'} Access {'>'} Service Auth,
                    create a new service token. Copy the Client ID and Client Secret (the secret is only shown once).
                  </div>
                  <div>
                    <strong>2. Create an Access Policy:</strong> In your Access Application {'>'} Policies,
                    create a policy with:
                    <ul className="list-disc list-inside ml-4 mt-1">
                      <li><strong>Action: Service Auth</strong> (not &quot;Allow&quot; - this is critical)</li>
                      <li>Selector: Service Token</li>
                      <li>Value: Select your service token name</li>
                    </ul>
                  </div>
                  <div>
                    <strong>3. Add Integration Here:</strong> Enter the App Domain and credentials from step 1.
                  </div>
                </div>

                <Text size="sm" className="text-muted-foreground pt-2">
                  Service token headers are sent automatically on requests to the app domain, so
                  service-auth-only policies work without any extra configuration.
                </Text>

                <a
                  href="https://developers.cloudflare.com/cloudflare-one/identity/service-tokens/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  Cloudflare Service Tokens Documentation
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
            </Flex>
          </CardContent>
        </Card>
      </Box>

      {/* Create/Edit Dialog */}
      <DialogTrigger
        isOpen={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setEditingIntegration(null);
            resetForm();
          }
        }}
      >
        <span />
        <Dialog title={editingIntegration ? 'Edit Integration' : 'Add Cloudflare Access Integration'}>
          <DialogContent>
            <Box className="space-y-4">
              <TextField
                label="Name"
                placeholder="e.g., Production App, Staging Environment"
                value={formData.name}
                onChange={(v) => setFormData((f) => ({ ...f, name: v }))}
                isDisabled={isPending}
                autoFocus
              />
              <TextField
                label="App Domain"
                placeholder="e.g., app.example.com"
                value={formData.appDomain}
                onChange={(v) => setFormData((f) => ({ ...f, appDomain: v }))}
                isDisabled={isPending}
                description="The domain protected by Cloudflare Access"
              />
              <PasswordField
                label={editingIntegration ? 'Client ID (leave blank to keep existing)' : 'Client ID'}
                placeholder="Service token client ID"
                value={formData.clientId}
                onChange={(v) => setFormData((f) => ({ ...f, clientId: v }))}
                isDisabled={isPending}
              />
              <PasswordField
                label={editingIntegration ? 'Client Secret (leave blank to keep existing)' : 'Client Secret'}
                placeholder="Service token client secret"
                value={formData.clientSecret}
                onChange={(v) => setFormData((f) => ({ ...f, clientSecret: v }))}
                isDisabled={isPending}
              />
            </Box>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => {
                setIsCreateOpen(false);
                setEditingIntegration(null);
                resetForm();
              }}
              isDisabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleSubmit}
              isDisabled={isPending || !isFormValid}
            >
              {isPending ? 'Saving...' : editingIntegration ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </>
  );
}
